import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Bell, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { API_BASE_URL } from '../../services/apiClient';

// periodicity_days: how long a notification stays live — it's shown on every
// login from creation (or re-activation) until this many days have passed.
const PERIODICITY_PRESETS = [
  { days: 1, label: '1 día' },
  { days: 7, label: '1 semana' },
  { days: 15, label: '15 días' },
  { days: 30, label: '1 mes' },
];

const periodicityLabel = (days) =>
  PERIODICITY_PRESETS.find((p) => p.days === days)?.label || `${days} días`;

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' });
};

const isExpired = (n) => n.active_until && new Date(n.active_until) <= new Date();

const EMPTY_FORM = { message: '', link_url: '', link_label: '', periodicity_days: 30, is_active: true };

export default function NotificationsPanel({ authToken }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [customPeriodicity, setCustomPeriodicity] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/notifications`, { headers });
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('[NotificationsPanel] Error fetching notifications:', err);
      toast.error('No se pudieron cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setCustomPeriodicity(false);
  };

  const startEdit = (n) => {
    setForm({
      message: n.message || '',
      link_url: n.link_url || '',
      link_label: n.link_label || '',
      periodicity_days: n.periodicity_days ?? 30,
      is_active: n.is_active !== false,
    });
    setEditingId(n.id);
    setCustomPeriodicity(!PERIODICITY_PRESETS.some((p) => p.days === n.periodicity_days));
  };

  const payloadFrom = (f) => ({
    message: f.message.trim(),
    link_url: f.link_url.trim() || null,
    link_label: f.link_label.trim() || null,
    periodicity_days: Number(f.periodicity_days) || 1,
    is_active: f.is_active,
  });

  const handleSave = async () => {
    if (!form.message.trim()) {
      toast.error('El mensaje es obligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/api/admin/notifications/${editingId}`, payloadFrom(form), { headers });
        toast.success('Notificación actualizada');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/notifications`, payloadFrom(form), { headers });
        toast.success('Notificación creada');
      }
      resetForm();
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'No se pudo guardar la notificación');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (n, isActive) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/notifications/${n.id}`,
        payloadFrom({ ...n, link_url: n.link_url || '', link_label: n.link_label || '', is_active: isActive }),
        { headers }
      );
      fetchNotifications();
    } catch (err) {
      toast.error('No se pudo actualizar la notificación');
    }
  };

  const handleDelete = async (n) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/notifications/${n.id}`, { headers });
      toast.success('Notificación eliminada');
      if (editingId === n.id) resetForm();
      fetchNotifications();
    } catch (err) {
      toast.error('No se pudo eliminar la notificación');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          {editingId ? <Pencil className="w-5 h-5 text-blue-400" /> : <Plus className="w-5 h-5 text-blue-400" />}
          {editingId ? 'Editar notificación' : 'Nueva notificación'}
        </h3>
        <p className="text-slate-500 text-sm mb-5">
          Se muestra a pantalla completa en cada inicio de sesión, solo a usuarios con suscripción activa pagada
          (no a accesos gratuitos otorgados por un admin), durante el periodo elegido desde que se crea o se reactiva.
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Mensaje</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              maxLength={1000}
              className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm p-3"
              placeholder="Tu suscripción ahora incluye..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Enlace (opcional)</label>
              <Input
                value={form.link_url}
                onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="https://..."
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Texto del botón del enlace</label>
              <Input
                value={form.link_label}
                onChange={(e) => setForm({ ...form, link_label: e.target.value })}
                placeholder="Ingresa aquí"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Periodo visible</label>
              <select
                value={customPeriodicity ? 'custom' : String(form.periodicity_days)}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setCustomPeriodicity(true);
                  } else {
                    setCustomPeriodicity(false);
                    setForm({ ...form, periodicity_days: Number(e.target.value) });
                  }
                }}
                className="h-9 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3"
              >
                {PERIODICITY_PRESETS.map((p) => (
                  <option key={p.days} value={String(p.days)}>{p.label}</option>
                ))}
                <option value="custom">Personalizada (días)</option>
              </select>
            </div>
            {customPeriodicity && (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Días</label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={form.periodicity_days}
                  onChange={(e) => setForm({ ...form, periodicity_days: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white w-28"
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-300 h-9">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Activa
            </label>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? 'Guardar cambios' : 'Crear notificación'}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm} className="border-slate-700 text-slate-300">
                <X className="w-4 h-4 mr-1" /> Cancelar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-400" />
            Notificaciones ({notifications.length})
          </h3>
          <Button variant="outline" size="sm" onClick={fetchNotifications} disabled={loading} className="border-slate-700 text-slate-300">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading && notifications.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">No hay notificaciones todavía.</p>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <div key={n.id} className="bg-slate-950/50 border border-slate-800 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-white whitespace-pre-line">{n.message}</p>
                    {n.link_url && (
                      <p className="text-xs text-blue-400 mt-1 truncate">
                        {n.link_label || 'Ingresa aquí'} → {n.link_url}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                        {periodicityLabel(n.periodicity_days)}
                      </span>
                      {!n.is_active ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400">Inactiva</span>
                      ) : isExpired(n) ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Vencida</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Activa</span>
                      )}
                      {n.is_active && (
                        <span className="text-[10px] text-slate-500">
                          {formatDate(n.active_from)} – {formatDate(n.active_until)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {n.is_active && !isExpired(n) ? (
                      <Button variant="outline" size="sm" onClick={() => toggleActive(n, false)} className="border-slate-700 text-slate-300 text-xs h-7">
                        Desactivar
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => toggleActive(n, true)} className="border-slate-700 text-slate-300 text-xs h-7">
                        {n.is_active ? 'Reactivar' : 'Activar'}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => startEdit(n)} className="border-slate-700 text-slate-300 h-7">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(n)} className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-7">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
